import React, { useEffect } from "react";
import { FEATURES_CONTENT } from "../data/featuresContent";

const C = {
  evergreen: "#163C2F",
  forest:    "#214D3A",
  sage:      "#7FB98E",
  ivory:     "#F5F1EA",
  brass:     "#B08D57",
  charcoal:  "#2B2B2B",
};

// Copy lives in src/data/featuresContent.js — shared with LandingPage.jsx's
// card grid and popup so the two can't drift apart. Keep this list in sync
// with that file only by editing featuresContent.js, never here.
const FEATURES = FEATURES_CONTENT;

export default function Features() {
  // No per-route head management anywhere in this app (Pricing/CookiePolicy/
  // PrivacyPolicy all inherit public/index.html's static tags) — set and
  // restore directly so this page gets its own title/description.
  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");

    document.title = "Features — Leasly Property Management";
    metaDesc?.setAttribute("content", "Everything Leasly does for landlords: tenant management, rent and payments, maintenance tracking, financial reports, document generation, and email/text receipts.");

    return () => {
      document.title = prevTitle;
      if (prevDesc != null) metaDesc?.setAttribute("content", prevDesc);
    };
  }, []);

  // Same pattern Pricing.jsx already uses for its #team anchor — scroll to the
  // requested section on load since this is a plain page, not a client router.
  useEffect(() => {
    if (!window.location.hash) return;
    const el = document.getElementById(window.location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const getStarted = () => { window.location.href = "/app?signin"; };

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif", background: C.ivory, minHeight: "100vh", color: C.charcoal }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Cormorant+Garamond:wght@400;600;700&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
      <style>{`
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        html{scroll-behavior:smooth;}
        body{-webkit-font-smoothing:antialiased;}
        .ft-nav{background:${C.evergreen};height:68px;display:flex;align-items:center;justify-content:space-between;padding:0 64px;position:sticky;top:0;z-index:30;border-bottom:1px solid rgba(255,255,255,0.06);}
        .ft-nav-logo{display:flex;align-items:center;gap:10px;text-decoration:none;cursor:pointer;}
        .ft-nav-logo-name{font-family:'Cormorant Garamond',serif;font-size:24px;font-weight:700;color:#F5F1EA;letter-spacing:0.02em;}
        .ft-nav-logo-sub{font-size:9px;color:${C.sage};letter-spacing:0.12em;text-transform:uppercase;margin-top:2px;}
        .ft-nav-links{display:flex;align-items:center;gap:28px;}
        .ft-nav-link{color:rgba(245,241,234,0.65);font-size:13px;font-weight:500;text-decoration:none;transition:color 0.15s;font-family:'Manrope',sans-serif;}
        .ft-nav-link:hover{color:#F5F1EA;}
        .ft-nav-link.active{color:#F5F1EA;font-weight:700;}
        .ft-nav-signin{background:transparent;color:#F5F1EA;border:1.5px solid ${C.brass};border-radius:50px;padding:8px 22px;font-family:'Manrope',sans-serif;font-size:12px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;transition:all 0.15s;}
        .ft-nav-signin:hover{background:${C.brass};color:${C.evergreen};}
        .ft-hero{text-align:center;padding:80px 40px 48px;background:${C.ivory};}
        .ft-eyebrow{display:inline-flex;align-items:center;gap:8px;border:1px solid ${C.brass}44;background:${C.brass}0d;border-radius:50px;padding:6px 18px;font-size:10px;letter-spacing:0.14em;text-transform:uppercase;color:${C.brass};margin-bottom:28px;font-family:'Manrope',sans-serif;font-weight:600;}
        .ft-hero-h1{font-family:'DM Serif Display',serif;font-size:clamp(38px,6vw,60px);font-weight:400;color:${C.evergreen};line-height:1.08;margin-bottom:16px;}
        .ft-hero-sub{font-size:16px;color:#5a7060;max-width:560px;margin:0 auto 32px;line-height:1.75;}
        .ft-hero-cta{background:${C.evergreen};color:${C.ivory};border:none;border-radius:50px;padding:14px 36px;font-weight:700;font-size:13px;cursor:pointer;letter-spacing:0.08em;text-transform:uppercase;font-family:'Manrope',sans-serif;transition:background 0.15s;}
        .ft-hero-cta:hover{background:${C.forest};}
        .ft-jump{position:sticky;top:68px;z-index:20;background:#fff;border-bottom:1px solid #E8E4DC;}
        .ft-jump-inner{max-width:1000px;margin:0 auto;padding:14px 40px;display:flex;gap:26px;overflow-x:auto;}
        .ft-jump-link{white-space:nowrap;font-size:13px;font-weight:600;color:#6b7c6b;text-decoration:none;transition:color 0.15s;}
        .ft-jump-link:hover{color:${C.evergreen};}
        .ft-body{max-width:1000px;margin:0 auto;padding:72px 40px 0;}
        .ft-section{padding-bottom:72px;border-bottom:1px solid #E8E4DC;margin-bottom:72px;scroll-margin-top:148px;}
        .ft-section:last-child{border-bottom:none;margin-bottom:0;}
        .ft-section-top{display:flex;align-items:center;gap:16px;margin-bottom:18px;}
        .ft-section-icon{width:54px;height:54px;flex-shrink:0;background:#EAF2ED;border:1px solid #C5DBC9;border-radius:14px;display:flex;align-items:center;justify-content:center;}
        .ft-section-title{font-family:'DM Serif Display',serif;font-size:clamp(26px,3.2vw,34px);font-weight:400;color:${C.evergreen};}
        .ft-section-intro{font-size:15px;color:#4a5e4a;line-height:1.8;max-width:680px;margin-bottom:24px;}
        .ft-bullets{display:flex;flex-direction:column;gap:11px;max-width:680px;}
        .ft-bullet{display:flex;align-items:flex-start;gap:11px;font-size:14px;color:${C.charcoal};line-height:1.55;}
        .ft-bullet::before{content:'';width:7px;height:7px;background:${C.brass};border-radius:50%;flex-shrink:0;margin-top:6px;}
        .ft-closing{text-align:center;background:${C.forest};padding:72px 40px;margin-top:72px;}
        .ft-closing-h2{font-family:'DM Serif Display',serif;font-size:clamp(28px,4vw,42px);font-weight:400;color:${C.ivory};margin-bottom:14px;}
        .ft-closing-sub{font-size:15px;color:rgba(245,241,234,0.6);margin-bottom:32px;}
        .ft-closing-cta{background:${C.brass};color:${C.evergreen};border:none;border-radius:50px;padding:14px 36px;font-weight:700;font-size:13px;cursor:pointer;letter-spacing:0.08em;text-transform:uppercase;font-family:'Manrope',sans-serif;transition:background 0.15s;}
        .ft-closing-cta:hover{background:#c9a568;}
        .ft-footer{background:${C.evergreen};border-top:1px solid rgba(255,255,255,0.08);padding:32px 64px;display:flex;justify-content:space-between;align-items:center;}
        .ft-footer-logo{font-family:'Cormorant Garamond',serif;font-size:20px;font-weight:700;color:#F5F1EA;}
        .ft-footer-sub{font-size:11px;color:rgba(245,241,234,0.35);margin-top:3px;}
        .ft-footer-copy{font-size:12px;color:rgba(245,241,234,0.3);}
        @media(max-width:900px){
          .ft-nav{padding:0 20px;}
          .ft-nav-links a:not(.ft-nav-signin){display:none;}
          .ft-hero{padding:56px 20px 36px;}
          .ft-jump-inner{padding:12px 20px;}
          .ft-body{padding:48px 20px 0;}
          .ft-section-top{align-items:flex-start;}
          .ft-closing{padding:56px 20px;}
          .ft-footer{padding:24px 20px;flex-direction:column;gap:12px;text-align:center;}
        }
      `}</style>

      {/* ── Nav ── */}
      <nav className="ft-nav">
        <a href="/" className="ft-nav-logo">
          <img src="/Leasly-logofinal.png" alt="Leasly" width="30" height="30" style={{ borderRadius: 6, objectFit: "contain" }}/>
          <div>
            <div className="ft-nav-logo-name">Leasly</div>
            <div className="ft-nav-logo-sub">by Gibbs Prodigy Group</div>
          </div>
        </a>
        <div className="ft-nav-links">
          <a href="/features" className="ft-nav-link active">Features</a>
          <a href="/pricing" className="ft-nav-link">Pricing</a>
          <a href="/#contact" className="ft-nav-link">Share an Idea</a>
          <button className="ft-nav-signin" onClick={getStarted}>Sign In</button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="ft-hero">
        <div className="ft-eyebrow">What We Offer</div>
        <h1 className="ft-hero-h1">Every tool a landlord<br/>actually needs.</h1>
        <p className="ft-hero-sub">Six core modules built specifically for modern landlords — from single units to full portfolios.</p>
        <button className="ft-hero-cta" onClick={getStarted}>Get Started Free</button>
      </section>

      {/* ── Jump menu ── */}
      <div className="ft-jump">
        <div className="ft-jump-inner">
          {FEATURES.map(f => <a key={f.id} href={`#${f.id}`} className="ft-jump-link">{f.title}</a>)}
        </div>
      </div>

      {/* ── Feature sections ── */}
      <div className="ft-body">
        {FEATURES.map(f => (
          <section key={f.id} id={f.id} className="ft-section">
            <div className="ft-section-top">
              <div className="ft-section-icon">{f.icon}</div>
              <h2 className="ft-section-title">{f.title}</h2>
            </div>
            <p className="ft-section-intro">{f.intro}</p>
            <div className="ft-bullets">
              {f.bullets.map((b, i) => <div key={i} className="ft-bullet">{b}</div>)}
            </div>
          </section>
        ))}
      </div>

      {/* ── Closing CTA ── */}
      <section className="ft-closing">
        <h2 className="ft-closing-h2">Ready to get your weekends back?</h2>
        <p className="ft-closing-sub">Start free — no card required.</p>
        <button className="ft-closing-cta" onClick={getStarted}>Get Started Free</button>
      </section>

      {/* ── Footer ── */}
      <footer className="ft-footer">
        <div>
          <div className="ft-footer-logo">Leasly</div>
          <div className="ft-footer-sub">by Gibbs Prodigy Group · Pittsburg, CA</div>
        </div>
        <div className="ft-footer-copy">© 2026 Gibbs Prodigy Group. All rights reserved.</div>
      </footer>
    </div>
  );
}
